/**
 * Random — seeded PRNG (mulberry32).
 *
 * The host generates a seed at match start and broadcasts it in the WELCOME
 * packet. All peers construct an identical Random and route every random
 * call through it (`scene.rng.next()`), so simulations stay deterministic.
 *
 * Why mulberry32: tiny (10 lines), fast, no dependencies, statistically fine
 * for game logic. Not crypto.
 */
export class Random {
  /** @param {number|string} seed */
  constructor(seed) {
    this._state = (typeof seed === 'string' ? Random.hashString(seed) : seed) >>> 0;
    if (this._state === 0) this._state = 0x9e3779b9;
  }

  /** Float in [0, 1). */
  next() {
    let t = (this._state += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** Integer in [min, max] inclusive. */
  intBetween(min, max) {
    return Math.floor(this.next() * (max - min + 1)) + min;
  }

  /** Float in [min, max). */
  floatBetween(min, max) {
    return min + this.next() * (max - min);
  }

  pick(arr) {
    return arr[Math.floor(this.next() * arr.length)];
  }

  /** Shuffle in place (Fisher-Yates). */
  shuffle(arr) {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(this.next() * (i + 1));
      const tmp = arr[i]; arr[i] = arr[j]; arr[j] = tmp;
    }
    return arr;
  }

  /** Generate a fresh seed string suitable for sharing. */
  static newSeed() {
    return ((Math.random() * 0xffffffff) >>> 0).toString(16).padStart(8, '0');
  }

  /** Cheap string → uint32 hash (FNV-1a-ish). */
  static hashString(str) {
    let h = 2166136261 >>> 0;
    for (let i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return h >>> 0;
  }
}
